import { useEffect, useRef } from 'react';

import { toast } from 'react-toastify';
import { useShallow } from 'zustand/react/shallow';

import { useRoomContext } from '@src/contexts';

import useMagicPenSocketListeners from './useMagicPenSocketListeners';
import useParticipantNotifications from '../useParticipantNotifications';
import usePeerPermissionsSocketListener from './usePeerPermissionsSocketListener';
import useCalculatorSocketListener from './useCalculatorSocketListener';

import SocketService from '@src/services/SocketService';

import { checkIfTimePassed, formatCohostLabel, speechText } from '@src/utils';

import { createIcon } from '@src/helpers';

import {
  usePeersStore,
  usePollsStore,
  useDrawerStore,
  useRoomActionsStore,
  useNotificationStore,
  useClosedCaptionsStore,
  useRoomStore,
  useChatStore,
  useRecordingStore,
  useRoomConfigStore,
} from '@src/stores';

import { AnnotationIcon } from '@src/assets/icons';

// Types
import {
  IPeer,
  IPoll,
  TPeerAction,
  TUserType,
  TPollEventType,
  IVotingResults,
  ECommonSocketKeys,
  ISpeechToTextResponse,
  IUpdatePeerPermissionEvent,
  EPeerPermission,
  EUserType,
  EMessageOperations,
  EMessageType,
  ISocketCmd,
  ICurrentRecorder,
} from '@src/types';

// Hooks

const useCommonSocketListener = ({ isConnected = false }: { isConnected: boolean }) => {
  // Initialize participant notifications hook
  useParticipantNotifications({ isConnected });
  // Initialize Magic Pen socket listeners
  useMagicPenSocketListeners({ isConnected });
  // Initialize peer permissions socket listener
  usePeerPermissionsSocketListener({ isConnected });
  // Initialize calculator socket listeners
  useCalculatorSocketListener({ isConnected });

  const {
    currentPeerId,
    handleCmd,
    peerAction,
    setMicRequest,
    setCamRequest,
    setHasRequestedShare,
    setHasTempShareAccess,
  } = useRoomContext();

  const stopTyping = useRef<{ [userId: string]: NodeJS.Timeout }>({});

  const { onShareScreenClick, isPipActive } = useRoomActionsStore(
    useShallow((state) => ({
      onShareScreenClick: state.shareScreenSettings.onClick,
      isPipActive: state.pipSettings.isActive,
    }))
  );

  const { updatePeerInfo, setShouldRefetchPeerPermissions } = usePeersStore(
    useShallow((state) => ({
      updatePeerInfo: state.updatePeerInfo,
      setShouldRefetchPeerPermissions: state.setShouldRefetchPeerPermissions,
    }))
  );

  const { requestInfo, setRequestInfo, removeRequestInfoByPeerId } = useRoomStore(
    useShallow((state) => ({
      requestInfo: state.requestInfo,
      setRequestInfo: state.setRequestInfo,
      removeRequestInfoByPeerId: state.removeRequestInfoByPeerId,
    }))
  );

  const { setPolls, appendPoll, setHasUnvisitedPolls } = usePollsStore(
    useShallow((state) => ({
      setPolls: state.setPolls,
      appendPoll: state.appendPoll,
      setHasUnvisitedPolls: state.setHasUnvisitedPolls,
    }))
  );

  const { appendNotification, setMessagesNotifications } = useNotificationStore(
    useShallow((state) => ({
      appendNotification: state.appendNotification,
      setMessagesNotifications: state.setMessagesNotifications,
    }))
  );

  const { appendMessage, updateMessage, selectedReceiverData } = useChatStore(
    useShallow((state) => ({
      appendMessage: state.appendMessage,
      updateMessage: state.updateMessage,
      selectedReceiverData: state.selectedReceiverData,
    }))
  );

  const { setScreenRecordingSettings } = useRoomActionsStore(
    useShallow((state) => ({
      setScreenRecordingSettings: state.setScreenRecordingSettings,
    }))
  );
  const { setCurrentRecorder } = useRecordingStore(
    useShallow((state) => ({ setCurrentRecorder: state.setCurrentRecorder }))
  );

  useEffect(() => {
    if (!isConnected) return;

    const handleNewPeerJoined = (data: IPeer) => {
      if (!data) return;
      const { peer_info, stream_uid, record_id } = data;
      const id = data.id ?? peer_info?.peer_id;
      const peer: IPeer = {
        id,
        peer_name: peer_info?.peer_name,
        peer_info: peer_info,
        peer_uuid: peer_info?.peer_uuid ?? '',
        ...(stream_uid && { stream_uid }),
        ...(record_id && { record_id }),
      };
      usePeersStore.getState().appendPeer(peer_info.peer_id, peer);
      if (peer_info?.peer_recording) {
        useRoomActionsStore.getState().setScreenRecordingSettings({ isActive: true });
        speechText('Start Meeting recording');
      }
    };

    SocketService.on(ECommonSocketKeys.NEW_PEER_JOINED, handleNewPeerJoined);

    return () => {
      if (isConnected) {
        SocketService.off(ECommonSocketKeys.NEW_PEER_JOINED, handleNewPeerJoined);
      }
    };
  }, [isConnected]);

  useEffect(() => {
    if (!isConnected) return;

    const handleSpeechToText = (data: ISpeechToTextResponse) => {
      try {
        if ('errorCode' in data) {
          useClosedCaptionsStore.getState().setClosedCaptionsError(data.errorCode);
          return;
        }

        useRoomActionsStore.getState().transcriptionSettings.isActive &&
          useClosedCaptionsStore.getState().addOrUpdateSpeaker({
            peer_id: data.peer_id,
            peer_name: data.peer_name,
            text: data.text,
            isFinal: data.is_final,
          });
        if (useClosedCaptionsStore.getState().closedCaptionsError) {
          useClosedCaptionsStore.getState().setClosedCaptionsError('');
        }
      } catch (error) {
        console.error('Error in handleSpeechToText:', error);
      }
    };

    SocketService.on(ECommonSocketKeys.SPEECH_TO_TEXT, handleSpeechToText);

    return () => {
      if (isConnected) {
        SocketService.off(ECommonSocketKeys.SPEECH_TO_TEXT, handleSpeechToText);
      }
    };
  }, [isConnected]);

  useEffect(() => {
    if (!isConnected) {
      return;
    }

    const handlePeerAction = (data: {
      from_peer_name: string;
      from_peer_id: string;
      peer_id: string;
      action: TPeerAction;
      broadcast: boolean;
      message: string;
      deleteMessages?: boolean;
      user_type?: TUserType;
    }) => {
      const currentPeerUserType = usePeersStore.getState().peers.get(data.peer_id)?.peer_info?.user_type;

      switch (data.action) {
        case 'ban':
          if (data.peer_id !== currentPeerId && data.deleteMessages) {
            useChatStore.getState().removeUserMessages({ userId: data.peer_id, messageType: EMessageType.PUBLIC });
          } else {
            appendNotification({
              title: data.from_peer_name,
              text: `${data.from_peer_name} banned you from this room.`,
            });
            setTimeout(() => {
              useRoomActionsStore.getState().exitRoomSettings.onExitClick(false, true);
            }, 3000);
          }
          break;
        case 'eject':
          appendNotification({
            title: data.from_peer_name,
            text: `${data.from_peer_name} removed you from this room.`,
          });
          setTimeout(() => {
            useRoomActionsStore.getState().exitRoomSettings.onExitClick();
          }, 3000);
          break;
        case 'mute':
          peerAction({
            from_peer_name: data.from_peer_name,
            id: data.peer_id,
            action: data.action,
            emit: false,
            broadcast: data.broadcast,
            info: true,
            msg: data.message,
          });

          // Show notification only if someone else muted you and your mic was actually on
          if (usePeersStore.getState().peers.get(currentPeerId)?.peer_info?.peer_audio) {
            appendNotification({
              title: '',
              text: `You were muted by the ${formatCohostLabel(data.user_type || '')}.`,
              source: 'mute',
            });
          }

          break;
        case 'requestOpenCamera':
          setCamRequest({
            from_peer_name: data.from_peer_name,
            from_peer_id: data.from_peer_id,
            roleLabel:
              data.user_type || usePeersStore.getState().peers.get(data.from_peer_id)?.peer_info.user_type || '',
            id: 'open_cam_request',
          });

          break;
        case 'requestToUnmute':
          setMicRequest({
            from_peer_name: data.from_peer_name,
            from_peer_id: data.from_peer_id,
            roleLabel:
              data.user_type || usePeersStore.getState().peers.get(data.from_peer_id)?.peer_info.user_type || '',
            id: 'open_mic_request',
          });
          break;
        case 'stopScreenSharingAll':
          onShareScreenClick(false);
          break;
        case 'updateUserType':
          appendNotification({
            title: '',
            text:
              data.user_type !== EUserType.ATTENDEE
                ? `The host added you as a ${formatCohostLabel(data.user_type ?? '')}`
                : `The host removed you as a ${formatCohostLabel(currentPeerUserType ?? '')}`,
            source: 'user-type',
          });
          if (data.user_type === EUserType.CO_HOST) {
            setHasRequestedShare(false);
          }

          //Fetch room settings for user types in case one got promoted or demoted
          SocketService.request('getRoomWithConfig', {
            room_id: useRoomStore.getState().roomId,
          })
            .then((data) => {
              const config = data?.room?.roomConfig || data?.roomConfig;
              config &&
                useRoomConfigStore.setState({
                  mainConfig: config.main,
                  chatConfig: config.chat,
                  pollConfig: config.poll,
                  questionsConfig: config.questions,
                  participantsListConfig: config.participantsList,
                  magicPenConfig: config.magicPen,
                  calculatorConfig: config.calculator,
                });
            })
            .catch((err) => {
              console.error('Get room config:', err);
            });

          updatePeerInfo(data.peer_id, { user_type: data.user_type });
          break;
        default:
          peerAction({
            from_peer_name: data.from_peer_name,
            id: data.peer_id,
            action: data.action,
            emit: false,
            broadcast: data.broadcast,
            info: true,
            msg: data.message,
          });
          break;
      }
    };
    SocketService.on(ECommonSocketKeys.PEER_ACTION, handlePeerAction);
    return () => {
      SocketService.off(ECommonSocketKeys.PEER_ACTION, handlePeerAction);
    };
  }, [
    appendNotification,
    currentPeerId,
    isConnected,
    onShareScreenClick,
    peerAction,
    setCamRequest,
    setHasRequestedShare,
    setMicRequest,
    updatePeerInfo,
  ]);

  useEffect(() => {
    if (!isConnected) {
      return;
    }

    const handlePolls = ({
      poll,
      polls,
      eventType,
      sourceName,
      eventSource,
      votingResults,
      targetPollId,
      targetPollIndex,
    }: {
      poll?: IPoll;
      polls?: IPoll[];
      sourceName: string;
      eventSource: string;
      targetPollId?: string;
      targetPollIndex: number;
      votingResults?: IVotingResults[];
      eventType: TPollEventType;
    }) => {
      const peer_id = SocketService.getSocketId();
      // for create a new poll on webinar
      if (poll?.id) {
        appendPoll(poll);
        // for create a new poll for meet
      } else if (Array.isArray(polls)) {
        setPolls(
          polls.map((poll) => ({
            ...poll,
            isVoteClosed:
              poll.closingTime === 'none'
                ? false
                : checkIfTimePassed({ createdAt: poll.createdAt, duration: poll.closingTime }),
          }))
        );
        // for update polls on webinar
      } else if (Array.isArray(votingResults)) {
        const currentPolls = usePollsStore.getState().polls;
        if (!currentPolls.length) return;
        const updatedPolls = currentPolls.map((poll) => {
          if (poll.id === targetPollId) {
            return {
              ...poll,
              votingResults,
            };
          }
          return poll;
        });
        setPolls(updatedPolls);
      }
      switch (eventType) {
        case 'CREATE_POLL':
          if (eventSource !== peer_id) {
            appendNotification({
              title: 'New poll',
              text: `${sourceName} has created a new poll. Cast your vote now!`,
              source: 'polls',
              id: 'newPoll',
            });
          }
          break;
        case 'VOTE':
          if (eventSource !== peer_id && !!usePeersStore.getState().isHostUser) {
            appendNotification({
              title: 'New vote',
              text: `${sourceName} has voted in Poll ${targetPollIndex + 1}`,
              source: 'polls',
              id: 'newVote',
            });
          }
          break;
        default:
          break;
      }
      if (eventSource !== peer_id) {
        const { selectedKey, isDrawerOpen } = useDrawerStore.getState();
        if (!(isDrawerOpen && selectedKey === 'polls')) {
          setHasUnvisitedPolls(true);
        }
      }
    };

    SocketService.on(ECommonSocketKeys.UPDATE_POLLS, handlePolls);

    return () => {
      if (isConnected) {
        SocketService.off(ECommonSocketKeys.UPDATE_POLLS, handlePolls);
      }
    };
  }, [appendNotification, appendPoll, isConnected, setHasUnvisitedPolls, setPolls]);

  const handleDeleteMessage = (data: {
    messageId: string;
    type: EMessageType;
    peer_id: string;
    to_peer_id: string;
  }) => {
    const { deleteMessage } = useChatStore.getState();
    const { removeNotifications } = useNotificationStore.getState();

    const mySocketId = SocketService.getSocketId();

    const receivedChat =
      data.type === EMessageType.PRIVATE ? (data.peer_id === mySocketId ? data.to_peer_id : data.peer_id) : data.type;

    deleteMessage({
      messageId: data.messageId,
      to: receivedChat,
    });

    removeNotifications(receivedChat, data.messageId);
  };

  useEffect(() => {
    if (!isConnected) {
      return;
    }
    const handleSocketMessage = (data: any) => {
      const receivedChat = data.type === EMessageType.PRIVATE ? data.peer_id : data.type;

      const { selectedKey, isDrawerOpen } = useDrawerStore.getState();
      const { updateTypingState } = useChatStore.getState();

      if (data.operation === EMessageOperations.UPDATE) {
        updateMessage({ messageId: data.chatMessageId, to: receivedChat, updateField: data.operationData });
      } else {
        updateTypingState(selectedReceiverData.value, '');
        setMessagesNotifications({
          peer: receivedChat,
          messages: [data],
          isDrawerOpen,
          selectedKey,
          messageType: data.type as EMessageType,
          selectedReceiver: selectedReceiverData,
          isPipActive,
        });
        appendMessage(data);
      }
    };

    const handleTypingIndication = (data: {
      eventType: string;
      sourceName: string;
      eventSource: string;
      message: string;
    }) => {
      if (data.eventType === 'CHAT_TYPING_INDICATOR') {
        const { eventSource, message } = data;

        stopTyping.current[eventSource] && clearTimeout(stopTyping.current[eventSource]);

        useChatStore.getState().updateTypingState(eventSource, message || '');

        stopTyping.current[eventSource] = setTimeout(() => {
          useChatStore.getState().updateTypingState(eventSource, '');
        }, 1000);
      }
    };

    SocketService.on(ECommonSocketKeys.MESSAGE, handleSocketMessage);
    SocketService.on(ECommonSocketKeys.DELETE_MESSAGE, handleDeleteMessage);
    SocketService.on(ECommonSocketKeys.USER_TYPING, handleTypingIndication);

    return () => {
      SocketService.off(ECommonSocketKeys.MESSAGE, handleSocketMessage);
      SocketService.off(ECommonSocketKeys.DELETE_MESSAGE, handleDeleteMessage);
      SocketService.off(ECommonSocketKeys.USER_TYPING, handleTypingIndication);
    };
  }, [
    appendMessage,
    isConnected,
    selectedReceiverData,
    selectedReceiverData.value,
    setMessagesNotifications,
    updateMessage,
    isPipActive,
  ]);

  useEffect(() => {
    if (!isConnected) return;

    const handleCmdData = (data: ISocketCmd) => {
      handleCmd(data);
    };

    SocketService.on(ECommonSocketKeys.CMD, handleCmdData);

    return () => {
      SocketService.off(ECommonSocketKeys.CMD, handleCmdData);
    };
  }, [handleCmd, isConnected]);

  useEffect(() => {
    if (!isConnected) return;

    const handleEditMessage = async (data: any) => {
      try {
        const myId = SocketService.getSocketId();
        const toKey =
          data.type === EMessageType.PRIVATE ? (data.peer_id === myId ? data.to_peer_id : data.peer_id) : data.type;

        // Process file data to create blob URL if fileBuffer exists (to have a valid url in case the file/attachment is edited)
        // This enables the view/download functionality in the edited message bubble
        let processedFile = data.file;
        if (data.file?.fileBuffer) {
          try {
            // fetch() is used to convert the base64 fileBuffer to a blob
            const response = await fetch(data.file.fileBuffer);
            const blob = await response.blob();
            const fileUrl = URL.createObjectURL(blob);

            processedFile = {
              ...data.file,
              fileUrl,
            };
          } catch (fileError) {
            console.error('Error processing file in edit message:', fileError);
            processedFile = data.file;
          }
        }

        useChatStore.getState().updateMessage({
          messageId: data.chatMessageId,
          to: toKey,
          updateField: {
            peer_msg: data.peer_msg,
            edited: data.edited,
            file: processedFile,
            originalTimestamp: data.originalTimestamp || data.timestamp,
            isPinned: data.isPinned,
          },
        });
        useChatStore.getState().setEditingMessageId(null);
      } catch (err) {
        console.error('Error in handleEditMessage:', err);
      }
    };

    SocketService.on(ECommonSocketKeys.EDIT_MESSAGE, handleEditMessage);
    return () => {
      SocketService.off(ECommonSocketKeys.EDIT_MESSAGE, handleEditMessage);
    };
  }, [isConnected]);

  useEffect(() => {
    if (!isConnected) return;

    const handleRespondShare = ({ message, requestId, eventSource }: any) => {
      setRequestInfo({ peerId: eventSource, requestId: requestId });

      appendNotification({
        title: '',
        text: message,
        source: 'share-screen',
        requestId: requestId,
      });
    };
    SocketService.on(ECommonSocketKeys.REQUEST_TO_ADMIT_ACTION, handleRespondShare);

    return () => {
      SocketService.off(ECommonSocketKeys.REQUEST_TO_ADMIT_ACTION, handleRespondShare);
    };
  }, [appendNotification, isConnected, requestInfo, setRequestInfo]);

  useEffect(() => {
    if (!isConnected) return;

    const handleAdminResponse = (data: any) => {
      console.log({ data });
      const status = data.status;
      const isApproved = status === 'APPROVED';
      if (isApproved) {
        appendNotification({
          title: '',
          text: `Request approved`,
          source: 'share-screen',
        });

        //Store request info for the peer who requested share screen, to be sent to "SHARE_SCREEN" Socket for the validation (speaker only)
        setRequestInfo({ peerId: currentPeerId, requestId: data.requestId });
      } else {
        appendNotification({
          title: '',
          text: `Request denied`,
          source: 'share-screen',
        });
      }

      setHasTempShareAccess(isApproved);
      setHasRequestedShare(false);
    };

    SocketService.on(ECommonSocketKeys.ADMIT_ACTION, handleAdminResponse);
    return () => {
      SocketService.off(ECommonSocketKeys.ADMIT_ACTION, handleAdminResponse);
    };
  }, [appendNotification, currentPeerId, isConnected, setHasRequestedShare, setHasTempShareAccess, setRequestInfo]);

  useEffect(() => {
    if (!isConnected) return;

    const handleRemoveNotification = ({ requestId, eventSource }: { requestId: string; eventSource: string }) => {
      toast.dismiss(requestId);
      toast.dismiss(`${requestId}-pip`);
      removeRequestInfoByPeerId(eventSource);
      useNotificationStore.getState().removeNotificationByRequestId(requestId);
    };

    SocketService.on(ECommonSocketKeys.REQUESTED_ACTION_PROCESSED, handleRemoveNotification);
    return () => {
      SocketService.off(ECommonSocketKeys.REQUESTED_ACTION_PROCESSED, handleRemoveNotification);
    };
  }, [isConnected, removeRequestInfoByPeerId]);

  useEffect(() => {
    if (!isConnected) return;

    const handleStopShareScreen = () => {
      onShareScreenClick(false);
    };

    SocketService.on(ECommonSocketKeys.STOP_SCREEN_SHARING_ALL, handleStopShareScreen);
    return () => {
      SocketService.off(ECommonSocketKeys.STOP_SCREEN_SHARING_ALL, handleStopShareScreen);
    };
  }, [isConnected, onShareScreenClick]);

  // Handle explicit Magic Pen annotate permission updates
  useEffect(() => {
    if (!isConnected) return;

    const handleUpdatePeerPermission = (data: IUpdatePeerPermissionEvent) => {
      switch (data.permission) {
        // Peer receives this event when the host grants or revokes Magic Pen annotate permission for them.
        case EPeerPermission.MAGIC_PEN_ALLOW_ANNOTATE:
          // Skip if current peer is the source (host who initiated the permission change)
          if (data.eventSource === currentPeerId) return;

          // This event is targeted to the current peer; flip Magic Pen annotate permission accordingly
          const granted = !!data.isGranted;
          useRoomStore.getState().setHasMagicPenAnnotatePermission(granted);

          appendNotification({
            title: '',
            text: granted ? 'You can now annotate on the shared screen' : 'Your annotation access has been removed',
            source: 'annotation',
            icon: createIcon({ icon: AnnotationIcon, size: '28px' }),
          });
          // Trigger refetch of permissions list so the modal can update in real-time
          setShouldRefetchPeerPermissions(true);
          break;
        // Cohost receives this event when the host leaves the room(granted), or rejoins(revoked), to let him take over the host role.
        case EPeerPermission.ACTING_HOST:
          useRoomStore.getState().setIsActingHost(data.isGranted);
          // Trigger refetch of permissions list to update ACTING_HOST status in the modal
          setShouldRefetchPeerPermissions(true);
          break;
      }
    };

    SocketService.on(ECommonSocketKeys.UPDATE_PEER_PERMISSION, handleUpdatePeerPermission);
    return () => {
      SocketService.off(ECommonSocketKeys.UPDATE_PEER_PERMISSION, handleUpdatePeerPermission);
    };
  }, [appendNotification, currentPeerId, isConnected, setShouldRefetchPeerPermissions]);

  // Handle magicPenPermission event - triggered when permissions are updated
  useEffect(() => {
    if (!isConnected) return;

    const handleMagicPenPermission = (data: {
      eventType: string;
      eventSource: string;
      sourceName: string;
      uuIds?: string[];
      grantedBy?: string;
      permission?: string;
      grantedAt?: string;
      isGranted?: boolean;
    }) => {
      // Trigger refetch of permissions list so the modal can update in real-time
      setShouldRefetchPeerPermissions(true);
    };

    SocketService.on(ECommonSocketKeys.MAGIC_PEN_PERMISSION, handleMagicPenPermission);
    return () => {
      SocketService.off(ECommonSocketKeys.MAGIC_PEN_PERMISSION, handleMagicPenPermission);
    };
  }, [isConnected, setShouldRefetchPeerPermissions]);

  useEffect(() => {
    if (!isConnected) return;

    const handleRecordingAction = (data: ICurrentRecorder) => {
      const isStopRecording = data.action === 'Stop conference recording';
      setCurrentRecorder(
        isStopRecording
          ? {
              peer_name: '',
              peer_id: '',
              action: '',
            }
          : data
      );

      const myId = SocketService.getSocketId();
      const isForcedStopByMe = isStopRecording && data.initiator_peer_id === myId && data.peer_id !== myId;

      const isWebinar = useRoomStore.getState().meetingType === 'webinar';
      const iAmAffected = isStopRecording && data.peer_id === myId;

      if (isWebinar && isStopRecording && iAmAffected && data.initiator_peer_id !== myId) {
        appendNotification({
          title: 'Meeting Recording',
          text: 'The host stopped your recording session.',
          source: 'recording',
        });
      }

      const shouldNotify = !isForcedStopByMe && !(isWebinar && iAmAffected);

      if (shouldNotify) {
        appendNotification({
          title: 'Meeting Recording',
          text: isStopRecording
            ? `This meeting has stopped being recorded.`
            : `This meeting is now starting to be recorded.`,
          source: 'recording',
        });
      }

      if (data.action === 'Start Meeting recording') {
        speechText('Start Meeting recording');
      }
      // In case current user get terminated let the useRecording stop the recording and make sure we clean the state up.
      if (data.peer_id !== SocketService.getSocketId()) {
        setScreenRecordingSettings({ isActive: !isStopRecording });
      }
      updatePeerInfo(data.peer_id, { peer_recording: !isStopRecording });
    };

    SocketService.on(ECommonSocketKeys.RECORDING_ACTION, handleRecordingAction);

    return () => {
      SocketService.off(ECommonSocketKeys.RECORDING_ACTION, handleRecordingAction);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  useEffect(() => {
    if (!isConnected) return;

    const handleForceStopScreenSharing = (data?: { user_type?: string }) => {
      onShareScreenClick(false);
      if (data?.user_type) {
        appendNotification({
          title: '',
          text: `Your screen sharing was stopped by the ${formatCohostLabel(data.user_type)}.`,
          source: 'share-screen',
        });
      }
    };

    SocketService.on(ECommonSocketKeys.FORCE_STOP_SCREEN_SHARING, handleForceStopScreenSharing);
    return () => {
      SocketService.off(ECommonSocketKeys.FORCE_STOP_SCREEN_SHARING, handleForceStopScreenSharing);
    };
  }, [appendNotification, isConnected, onShareScreenClick]);
};

export default useCommonSocketListener;
